/**
 * Send to edit (1.0.0-rc.6): the dialog that hands a sequence (from the editor) or clips (from the Library) to an
 * NLE through an edit location, and the "Sent to edit" list on a sequence's Exports tab.
 *
 * 1.0.0-rc.9: the dialog picks the location's workstation mapping ("Editors open it with"), not an operating system;
 * each ready hand-off has Download timeline (a format for any of the location's mappings) and Copy path.
 *
 * rc.9 fixes: the dialog picks the NLE (remembered) and one format it reads, so a hand-off has one timeline; Download
 * timeline makes any other. Trimmed media is the default. Hand-offs are rows of Create's Exports list (HandoffRow).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Check, ChevronDown, Clapperboard, Copy, Download, FileInput, HelpCircle, Loader2, X } from 'lucide-react';
import {
  HANDOFF_FIELD_ORDERS, HANDOFF_FIELD_ORDER_LABELS, MEDIA_MODES, MEDIA_MODE_LABELS, NLES, type HandoffFieldOrder, NLE_FORMATS, NLE_FORMAT_INFO, NLE_FORMAT_SHORT, NLE_INFO, browserOs, chooseMapping, joinWorkstationPath, mappingLabel,
  type EditDestinationDTO, type HandoffDTO, type MediaMode, type Nle, type NleFormat, type TargetOs, type WorkstationMappingSnapshot,
} from '@prism/contracts';
import { useDismissOnOutside } from '../lib/useDismissOnOutside';
import { api, errorMessage } from '../api/client';
import { useEditDestinations, useHandoffs } from '../api/hooks';
import { queryKeys } from '../api/queryKeys';
import { copy } from '../lib/clipboard';
import { bytes, relative } from '../lib/format';
import { usePref } from '../lib/prefs';
import { Modal } from './Modal';
import { PreflightProblems, RightsCheckBlock, useRightsCheck, type PreflightResult } from './Preflight';
import { toast } from './Toasts';
import { CheckInDialog, REMOVE_HANDOFF_TITLE, removeHandoff, restoreHandoff } from './CheckIn';
import type { CheckinDTO, MasterChoiceOrAll } from '@prism/contracts';
import { MasterPicker } from './MasterPicker';

export type SendSubject =
  | {
    kind: 'sequence'; id: string; name: string; hasSubtitles: boolean; flush: () => Promise<void>;
    /** 1.7.0: the sequence's formats (formatsOf, the primary first); with more than one the dialog offers a Format choice. */
    formats?: Array<{ id: string; label: string; width: number; height: number; isPrimary: boolean }>;
    /** 1.7.4: the sequence has an In or Out, so "In to Out" can be sent instead of the whole sequence. */
    hasMarks?: boolean;
  }
  | { kind: 'clips'; assetIds: string[]; name: string };

const MODE_HELP: Record<MediaMode, string> = {
  proxy: 'Small files that travel fast: for remote and regional offices. Relink to the originals in the NLE for the online.',
  original: 'The full-quality originals, copied whole.',
  trimmed: 'Only the parts the sequence uses, plus handles, rewrapped without transcoding: for long sources.',
  in_place: 'No copies: the timelines link the originals where they are, on storage the editors also mount (read-only).',
};

/** The editors' operating system, from this browser's: most send to the computer they work on. */
export function guessTargetOs(platform = typeof navigator !== 'undefined' ? navigator.platform : ''): TargetOs {
  if (/^Win/i.test(platform)) return 'windows';
  if (/Linux/i.test(platform) && !/Android/i.test(platform)) return 'linux';
  return browserOs(typeof navigator !== 'undefined' ? navigator.userAgent : '');
}

/** Edit locations grouped for the picker (1.0.0-rc.9): on the PRISM server, on storage agents by site, buckets. */
function destinationGroups(list: EditDestinationDTO[]): Array<{ label: string; items: EditDestinationDTO[] }> {
  const groups: Array<{ label: string; items: EditDestinationDTO[] }> = [];
  const server = list.filter((d) => d.kind === 'fs');
  if (server.length) groups.push({ label: 'On the PRISM server', items: server });
  for (const site of [...new Set(list.filter((d) => d.kind === 'agent').map((d) => d.site))]) groups.push({ label: `Storage agents · site ${site}`, items: list.filter((d) => d.kind === 'agent' && d.site === site) });
  const buckets = list.filter((d) => d.kind === 's3' || d.kind === 'oci');
  if (buckets.length) groups.push({ label: 'Object storage (buckets)', items: buckets });
  return groups;
}

interface HandoffPreflight extends PreflightResult {
  warnings: string[]; estimateBytes: number; files: number; graphics: number;
  /** 1.0.0-rc.11: the default field order (from V1's media) and whether the sequence's rate can be interlaced. */
  fieldOrder?: { default: HandoffFieldOrder; interlacedAllowed: boolean };
}

export function SendToEditDialog({ subject, revision, onClose }: { subject: SendSubject; revision?: number; onClose: () => void }) {
  const qc = useQueryClient();
  const destinations = useEditDestinations().data?.items;
  // A person's own last choice (a regional office uses its own location), else the workspace's default.
  const [remembered, setRemembered] = usePref<string>('nle.editLocation', '');
  const [chosen, setChosen] = useState('');
  const locationId = chosen || (destinations?.find((d) => d.id === remembered)?.id ?? destinations?.find((d) => d.isDefault)?.id ?? destinations?.[0]?.id ?? '');
  // The NLE: this person's last choice; its best format unless another it reads is picked.
  const [rememberedNle, setRememberedNle] = usePref<string>('nle.app', 'resolve');
  const nle: Nle = (NLES as readonly string[]).includes(rememberedNle) ? rememberedNle as Nle : 'resolve';
  const [pickedFormat, setPickedFormat] = useState<NleFormat | null>(null);
  const format = pickedFormat && NLE_INFO[nle].formats.includes(pickedFormat) ? pickedFormat : NLE_INFO[nle].formats[0]!;
  const [mediaMode, setMediaMode] = useState<MediaMode>('trimmed');
  const [handles, setHandles] = useState('2');
  // The mapping: this person's last choice for the location, else its default for this computer's OS, else the first.
  const [lastMappings, setLastMappings] = usePref<Record<string, string>>('nle.mappings', {});
  const [pickedMapping, setPickedMapping] = useState<Record<string, string>>({});
  const [subtitles, setSubtitles] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const location = destinations?.find((d) => d.id === locationId);
  const mappings = location?.workstationMappings ?? [];
  const mapping = mappings.find((m) => m.id === pickedMapping[locationId]) ?? chooseMapping(mappings, { lastId: lastMappings[locationId] ?? null, os: guessTargetOs() });
  // Which master each clip's media comes from (1.2.0): default Programme, the format closest to the sequence or edit
  // format; proxies are always the primary's.
  const [masterValue, setMaster] = useState<MasterChoiceOrAll | null>(null);
  const master = mediaMode !== 'proxy' && masterValue && masterValue.type !== 'all' ? masterValue : null;
  // 1.7.0: which of the sequence's formats the timelines are made for (only when it has formats); 'primary' by default.
  const seqFormats = subject.kind === 'sequence' && (subject.formats?.length ?? 0) > 1 ? subject.formats! : null;
  const [seqFormat, setSeqFormat] = useState('primary');
  // 1.7.4: the whole sequence or its In to Out (the server crops the snapshot).
  const [rangeChoice, setRange] = useState<'all' | 'marks'>('all');
  const range = subject.kind === 'sequence' && subject.hasMarks ? rangeChoice : 'all';
  const formatId = seqFormats ? (seqFormats.some((f) => f.id === seqFormat) ? seqFormat : 'primary') : null;
  const body = useMemo(() => ({
    locationId, nle, formats: [format], mediaMode, handlesSeconds: Math.max(0, Number(handles) || 0), subtitles,
    ...(mapping ? { mappingId: mapping.id } : { mappingId: null, targetOs: guessTargetOs() }),
    ...(master ? { master } : {}),
    ...(formatId ? { formatId } : {}),
  }), [locationId, nle, format, mediaMode, handles, mapping, subtitles, master, formatId]);
  const preflight = useQuery({
    queryKey: ['preflight', 'handoff', subject.kind === 'sequence' ? subject.id : subject.assetIds, body],
    queryFn: () => api<HandoffPreflight>('/api/v1/handoffs/preflight', { method: 'POST', json: { ...body, ...(subject.kind === 'sequence' ? { sequenceId: subject.id } : { assetIds: subject.assetIds }) } }),
    enabled: Boolean(locationId),
    staleTime: 0, retry: false,
  });
  // The timelines' field order (1.0.0-rc.11): the default from the media on V1, until the person picks one.
  const [pickedFieldOrder, setPickedFieldOrder] = useState<HandoffFieldOrder | null>(null);
  const interlacedAllowed = preflight.data?.fieldOrder?.interlacedAllowed ?? true;
  const fieldOrder: HandoffFieldOrder = !interlacedAllowed ? 'progressive' : pickedFieldOrder ?? preflight.data?.fieldOrder?.default ?? 'progressive';
  const blocked = !locationId || preflight.isLoading || Boolean(preflight.data && !preflight.data.ok);
  const restorable = Boolean(preflight.data && !preflight.data.ok && preflight.data.restorable);
  // 1.8.0: the rights answers (confirm, a rights manager's override) the hand-off sends.
  const rights = useRightsCheck(preflight.data?.problems);

  async function send(restore = false) {
    setBusy(true);
    setError(null);
    try {
      let h: HandoffDTO;
      if (subject.kind === 'sequence') {
        await subject.flush();
        const cur = await api<{ revision: number }>(`/api/v1/sequences/${subject.id}`);
        h = await api<HandoffDTO>(`/api/v1/sequences/${subject.id}/handoffs`, { method: 'POST', json: { ...body, fieldOrder, revision: cur.revision ?? revision, ...(range === 'marks' ? { range } : {}), ...(restore ? { restore: true } : {}), ...rights.field() } });
      } else {
        h = await api<HandoffDTO>('/api/v1/assets/handoffs', { method: 'POST', json: { ...body, fieldOrder, assetIds: subject.assetIds, name: subject.name, ...(restore ? { restore: true } : {}), ...rights.field() } });
      }
      setRemembered(locationId);
      if (mapping) setLastMappings({ ...lastMappings, [locationId]: mapping.id });
      await qc.invalidateQueries({ queryKey: queryKeys.handoffs() });
      toast(restore ? 'Restoring the originals from the archive; the hand-off starts when they are back' : `Sending “${h.name}” to ${h.location.name}`);
      onClose();
    } catch (err) {
      setError(rights.takeError(err) ? 'Check the rights below first' : errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  const groups = destinationGroups(destinations ?? []);
  return (
    <Modal title={subject.kind === 'sequence' ? 'Send to edit' : `Send ${subject.assetIds.length} clip${subject.assetIds.length === 1 ? '' : 's'} to edit`} wide onClose={onClose}
      actions={<>
        <Link to="/help/nle#send" className="btn ghost sm" style={{ marginRight: 'auto' }} data-testid="send-help"><HelpCircle size={13} /> Help</Link>
        <button className="btn ghost" onClick={onClose}>Cancel</button>
        {restorable && (
          <button className="btn primary" onClick={() => void send(true)} disabled={busy || !rights.ready} data-testid="restore-and-send">
            {busy ? <Loader2 size={14} className="spin" /> : <Clapperboard size={14} />} Restore and send
          </button>
        )}
        <button className="btn primary" onClick={() => void send()} disabled={busy || blocked || !rights.ready} data-testid="start-send">
          {busy ? <Loader2 size={14} className="spin" /> : <Clapperboard size={14} />} Send to edit
        </button>
      </>}>
      <div data-testid="send-to-edit">
        {error && <div className="banner error">{error}</div>}
        {destinations && destinations.length === 0 && (
          <div className="banner warning" data-testid="no-edit-locations"><div className="banner-body"><strong>No edit locations yet</strong>
            <span>An administrator adds them in Administration → Edit locations.</span></div></div>
        )}
        <div className="field-row">
          <div className="field"><label htmlFor="se-location">Edit location</label>
            <select id="se-location" className="select" value={locationId} onChange={(e) => setChosen(e.target.value)} data-testid="send-location">
              {groups.map((g) => (
                <optgroup key={g.label} label={g.label}>
                  {g.items.map((d) => (
                    <option key={d.id} value={d.id}>{d.name}{d.isDefault ? ' (default)' : ''}{d.problem ? ` · ${d.problem}` : d.freeBytes != null ? ` · ${bytes(d.freeBytes)} free` : ''}</option>
                  ))}
                </optgroup>
              ))}
            </select>
            {location && (location.kind === 's3' || location.kind === 'oci') && <span className="help">A bucket: editors open the same files over SMB on a NAS that serves the bucket as a share too.</span>}
          </div>
          <div className="field"><label htmlFor="se-mapping">Editors open it with</label>
            {mappings.length > 0 ? (
              <select id="se-mapping" className="select" value={mapping?.id ?? ''} onChange={(e) => setPickedMapping({ ...pickedMapping, [locationId]: e.target.value })} data-testid="send-mapping">
                {mappings.map((m) => <option key={m.id} value={m.id}>{mappingLabel(m)}{m.isDefault ? ' (default)' : ''}</option>)}
              </select>
            ) : <div className="banner warning" style={{ margin: 0 }} data-testid="send-no-mapping"><div className="banner-body"><span>No workstation mapping: paths are the location's own; relink in the NLE.</span></div></div>}
            {mapping && <span className="help" data-testid="send-mapping-path">Timelines link <span className="mono">{joinWorkstationPath(mapping.path, '<hand-off>', 'media', 'clip.mov')}</span></span>}
          </div>
        </div>
        <div className="field-row">
          <div className="field"><label htmlFor="se-nle">NLE</label>
            <select id="se-nle" className="select" value={nle} onChange={(e) => { setRememberedNle(e.target.value); setPickedFormat(null); }} data-testid="send-nle">
              {NLES.map((n) => <option key={n} value={n}>{NLE_INFO[n].label}</option>)}
            </select>
            <span className="help">One timeline for it; Download timeline on the hand-off makes any other format later.</span>
          </div>
          <div className="field" style={{ flex: '0 0 auto', minWidth: 120 }}><label htmlFor="se-format">Format</label>
            <select id="se-format" className="select" value={format} onChange={(e) => setPickedFormat(e.target.value as NleFormat)} data-testid="send-format" title={NLE_FORMAT_INFO[format].label}>
              {NLE_INFO[nle].formats.map((f) => <option key={f} value={f}>{NLE_FORMAT_SHORT[f]}</option>)}
            </select>
          </div>
          <div className="field" style={{ flex: '0 0 auto', minWidth: 170 }}><label htmlFor="se-fields">Field order</label>
            <select id="se-fields" className="select" value={fieldOrder} disabled={!interlacedAllowed} onChange={(e) => setPickedFieldOrder(e.target.value as HandoffFieldOrder)} data-testid="send-field-order">
              {HANDOFF_FIELD_ORDERS.map((f) => <option key={f} value={f}>{HANDOFF_FIELD_ORDER_LABELS[f]}</option>)}
            </select>
            <span className="help" data-testid="send-field-order-help">{!interlacedAllowed ? 'Only 25 and 29.97 fps sequences can be interlaced.' : pickedFieldOrder ? 'The timeline’s field dominance.' : 'From the media on V1.'}</span>
          </div>
        </div>
        {seqFormats && (
          <div className="field" data-testid="send-sequence-format-field"><label htmlFor="se-seq-format">Sequence format</label>
            <select id="se-seq-format" className="select" value={formatId ?? 'primary'} onChange={(e) => setSeqFormat(e.target.value)} data-testid="send-sequence-format">
              {seqFormats.map((f) => <option key={f.id} value={f.id}>{f.label} · {f.width}×{f.height}{f.isPrimary ? ' (primary)' : ''}</option>)}
            </select>
            <span className="help">The timelines get this format's frame size. Reframing, keys and video track motion are not carried to the NLE yet: reframe there.</span>
          </div>
        )}
        {subject.kind === 'sequence' && (
          <div className="field"><label>Range</label>
            <div className="segmented" role="radiogroup" aria-label="Range" data-testid="send-range">
              <button aria-pressed={range === 'all'} onClick={() => setRange('all')} data-testid="send-range-all">Whole sequence</button>
              <button aria-pressed={range === 'marks'} onClick={() => setRange('marks')} disabled={!subject.hasMarks} data-testid="send-range-marks"
                title={subject.hasMarks ? 'From the sequence In to its Out; the edit checks back in as a new sequence' : 'Mark a sequence In or Out (I, O) first'}>In to Out</button>
            </div>
          </div>
        )}
        <div className="field"><label>Media</label>
          <div className="segmented" role="radiogroup" aria-label="Media" data-testid="send-media-mode">
            {MEDIA_MODES.map((m) => <button key={m} aria-pressed={mediaMode === m} onClick={() => setMediaMode(m)} data-testid={`send-mode-${m}`}>{m === 'in_place' ? 'In place' : m === 'trimmed' ? 'Trimmed' : MEDIA_MODE_LABELS[m]}</button>)}
          </div>
          <span className="help">{MODE_HELP[mediaMode]}</span>
        </div>
        {mediaMode !== 'proxy' && (
          <div className="field" data-testid="send-master"><label htmlFor="se-master">Master</label>
            <MasterPicker id="se-master" value={masterValue} onChange={setMaster} defaultLabel="Programme, closest to the sequence format" testId="send-master-picker" />
            <span className="help">For assets with several masters: which one the media is copied, trimmed or linked from. The check below names any clip without it.</span>
          </div>
        )}
        <div className="field-row">
          {mediaMode === 'trimmed' && (
            <div className="field"><label htmlFor="se-handles">Handles (seconds)</label>
              <input id="se-handles" className="input" type="number" min={0} max={600} step={0.5} value={handles} onChange={(e) => setHandles(e.target.value)} data-testid="send-handles" />
              <span className="help">Before and after each part used, so editors can trim and add transitions.</span></div>
          )}
          {(subject.kind === 'clips' || subject.hasSubtitles) && (
            <div className="field"><label>Subtitles</label>
              <label className="toggle-row"><input type="checkbox" checked={subtitles} onChange={(e) => setSubtitles(e.target.checked)} data-testid="send-subtitles" /><span>Include subtitles as SRT</span></label></div>
          )}
        </div>
        <p className="faint" style={{ fontSize: 12, margin: '4px 0 8px' }}>
          Graphics are rendered as ProRes 4444 with alpha on a "PRISM Graphics" track, so every NLE shows them as PRISM does.
          {preflight.data?.ok ? ` About ${bytes(preflight.data.estimateBytes)} in ${preflight.data.files} files${preflight.data.graphics ? `, ${preflight.data.graphics} graphic${preflight.data.graphics === 1 ? '' : 's'}` : ''}.` : ''}
        </p>
        <PreflightProblems q={preflight as never} what="This hand-off" />
        <RightsCheckBlock check={rights} />

        {(preflight.data?.warnings.length ?? 0) > 0 && (
          <div className="banner info" data-testid="send-warnings"><div className="banner-body">{preflight.data!.warnings.map((w, i) => <span key={i}>{w}</span>)}</div></div>
        )}
      </div>
    </Modal>
  );
}

/** Where a hand-off's timeline is on the editors' computers, for one mapping (null without one). */
export function timelinePath(h: Pick<HandoffDTO, 'folder'>, mapping: Pick<WorkstationMappingSnapshot, 'path'> | null, path: string): string | null {
  return mapping ? joinWorkstationPath(mapping.path, h.folder, path) : null;
}

/**
 * Download timeline (1.0.0-rc.9): each written format, for the mapping it was sent with and for the location's other
 * mappings (made from PRISM's database on request: another office gets its own paths). A .fcpxml on macOS opens in
 * Final Cut Pro; Copy path copies where the timeline is on the editors' computers. Since the rc.9 fixes a hand-off
 * writes one timeline: the other formats (not the AAF, the converter's) are made on request too, with no path to copy.
 */
export function DownloadTimelineMenu({ h }: { h: HandoffDTO }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  useDismissOnOutside(ref, open, () => setOpen(false));
  // The list sits low in the Exports pane: bring the whole menu into view when it opens.
  useEffect(() => { if (open) menu.current?.scrollIntoView({ block: 'nearest' }); }, [open]);
  const own = h.mapping;
  const others = h.mappings.filter((m) => m.id !== own?.id);
  const mappings: Array<WorkstationMappingSnapshot | null> = [own, ...others];
  const url = (format: NleFormat, m: WorkstationMappingSnapshot | null) => `/api/v1/handoffs/${h.id}/timeline?${new URLSearchParams({ format, ...(m ? { mappingId: m.id } : {}) })}`;
  return (
    <div className="menu-anchor" ref={ref}>
      <button type="button" className="btn sm" aria-haspopup="dialog" aria-expanded={open} onClick={() => setOpen(!open)} data-testid="download-timeline"><Download size={13} /> Download timeline <ChevronDown size={12} /></button>
      {open && (
        <div className="popover columns-popover timeline-menu" role="dialog" aria-label="Download timeline" ref={menu} data-testid="download-timeline-menu">
          {mappings.map((m, i) => (
            <section key={m?.id ?? 'none'}>
              <p className="section-label" style={{ margin: i ? '8px 0 4px' : '0 0 4px' }}>{m ? mappingLabel(m) : 'The location\'s own paths'}{i === 0 ? ' · as sent' : ''}</p>
              <ul>
                {NLE_FORMATS.flatMap((f) => {
                  const written = h.timelines.find((t) => t.format === f);
                  if (f === 'aaf' && (!written || i > 0)) return [];
                  return [{ format: f, written }];
                }).map((t) => {
                  const path = t.written ? timelinePath(h, m, t.written.path) : null;
                  return (
                    <li key={t.format}>
                      <a className="facet-option" href={url(t.format, m)} download data-testid={`download-${t.format}${i ? `-${m?.id}` : ''}`}>
                        {t.format === 'fcpxml' && m?.os === 'mac' ? 'Open in Final Cut Pro (.fcpxml)' : `${NLE_FORMAT_INFO[t.format].label} (.${NLE_FORMAT_INFO[t.format].extension})`}
                      </a>
                      {path && <button type="button" className="btn ghost icon sm" onClick={() => copy(path)} aria-label={`Copy the path of the ${NLE_FORMAT_INFO[t.format].label}`} title={path} data-testid={`copy-path-${t.format}${i ? `-${m?.id}` : ''}`}><Copy size={12} /></button>}
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
          <p className="faint" style={{ fontSize: 11, margin: '6px 0 0' }}>Premiere: File → Import. Resolve: File → Import → Timeline (set the frame rate first). Avid: import into a bin. <Link to="/help/nle#download">More</Link></p>
        </div>
      )}
    </div>
  );
}

const STEP_LABEL: Record<string, string> = {
  recording: 'Waiting for the recording to end', restoring: 'Waiting for originals from the archive', fetching: 'Fetching originals from site',
  media: 'Copying media', graphics: 'Rendering graphics', timelines: 'Writing timelines', delivering: 'Delivering to the site', done: 'Done',
};

/**
 * One hand-off as a row of Create's Exports list (rc.9 fixes; the "Sent to edit" list before): kind Send to edit, its
 * status and live progress (Copying media, Rendering graphics, Writing timelines), the edit location and mapping, where
 * editors find the folder with Copy path, Download timeline, the zip, Cancel and Check in (1.0.0-rc.7).
 */
export function HandoffRow({ h, onCheckin }: { h: HandoffDTO; onCheckin: (h: HandoffDTO) => void }) {
  const qc = useQueryClient();
  const running = h.status === 'queued' || h.status === 'running';
  const where = h.workstationFolder ?? `${h.location.name} · ${h.folder}`;
  const cancel = async () => {
    try {
      await api(`/api/v1/handoffs/${h.id}`, { method: 'DELETE' });
      await qc.invalidateQueries({ queryKey: queryKeys.handoffs() });
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  };
  return (
    <div className="step" data-status={h.status} data-testid="handoff-row">
      <span className={`step-icon ${h.status === 'ready' ? 'succeeded' : h.status === 'failed' || h.status === 'cancelled' ? 'failed' : 'running'}`}>
        {h.status === 'ready' ? <Check size={12} strokeWidth={3} /> : h.status === 'failed' || h.status === 'cancelled' ? <X size={12} strokeWidth={3} /> : <Loader2 size={12} className="spin" />}
      </span>
      <div className="step-main">
        <span className="step-title"><span data-testid="handoff-kind">Send to edit</span>{h.nle ? ` · ${NLE_INFO[h.nle].label}` : ''} · {h.location.name}{h.mapping ? ` · ${mappingLabel(h.mapping)}` : ''}</span>
        <span className="step-sub" data-testid="handoff-progress">
          {running ? `${STEP_LABEL[h.step ?? ''] ?? 'Queued'}${h.percent != null ? ` · ${Math.round(h.percent)}%` : ''}` : h.status === 'cancelled' ? `Cancelled ${relative(h.finishedAt)}` : h.status === 'failed' ? `Failed ${relative(h.finishedAt ?? h.createdAt)}` : relative(h.finishedAt ?? h.createdAt)}
          {' · '}{MEDIA_MODE_LABELS[h.mediaMode]}{h.files.bytes ? ` · ${bytes(h.files.bytes)}` : ''} · {h.formats.map((f) => NLE_FORMAT_SHORT[f]).join(', ')}{h.createdBy ? ` · ${h.createdBy.name}` : ''}
        </span>
        <span className="step-sub mono" style={{ display: 'flex', alignItems: 'center', gap: 4, overflow: 'hidden' }} data-testid="handoff-folder" title={where}>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{where}</span>
          <button className="btn ghost icon sm" onClick={() => copy(where)} aria-label="Copy the folder path" title="Copy the folder path" data-testid="copy-handoff-folder"><Copy size={12} /></button>
        </span>
        {h.error && <span className="step-sub" style={{ color: 'var(--danger)' }}><AlertTriangle size={11} /> {h.error.message}</span>}
        {h.warnings.map((w, i) => <span key={i} className="step-sub" style={{ color: 'var(--warning)' }}><AlertTriangle size={11} /> {w}</span>)}
        {(h.checkedInAt || h.cleanedAt) && <span className="step-sub" data-testid="handoff-checked-in">{h.checkedInAt ? `Checked in ${relative(h.checkedInAt)}` : ''}{h.checkedInAt && h.cleanedAt ? ' · ' : ''}{h.cleanedAt ? `Cleaned up ${relative(h.cleanedAt)}` : ''}</span>}
        {h.removedAt && <span className="step-sub faint" data-testid="handoff-removed">Removed from the Import list {relative(h.removedAt)}</span>}
        {!h.cleanedAt && h.folderCheck?.missing && <span className="step-sub" style={{ color: 'var(--warning)' }} data-testid="handoff-folder-missing"><AlertTriangle size={11} /> Folder is gone from {h.location.name} (missing since {relative(h.folderCheck.missingSince)}){h.removedAt ? '' : '; remove the hand-off from the Import list, or put the folder back'}.</span>}
        <span style={{ display: 'flex', gap: 6, marginTop: 4, flexWrap: 'wrap' }}>
          {h.packageUrl && <DownloadTimelineMenu h={h} />}
          {h.packageUrl && <a className="btn sm" href={h.packageUrl} download data-testid="handoff-zip"><Download size={13} /> ZIP{h.packageHasMedia ? ' with proxies' : ''}</a>}
          {running && <button className="btn ghost sm" onClick={() => void cancel()} data-testid="cancel-handoff">Cancel</button>}
          {h.status === 'ready' && !h.cleanedAt && !h.folderCheck?.missing && <button className="btn sm" onClick={() => onCheckin(h)} data-testid="checkin-handoff"><FileInput size={13} /> Check in…</button>}
          {h.canRemove && <button className="btn ghost sm" onClick={() => void removeHandoff(h, qc)} title={REMOVE_HANDOFF_TITLE} data-testid="remove-handoff">Remove from the Import list</button>}
          {h.canRestore && <button className="btn ghost sm" onClick={() => void restoreHandoff(h, qc)} data-testid="restore-handoff">Show in the Import list again</button>}
        </span>
      </div>
    </div>
  );
}

/** Check in from a hand-off row: the dialog, with the editor tab's id so it can apply a new version under its edit lock. */
export function useCheckinDialog(tabId?: string, onApplied?: (c: CheckinDTO) => void) {
  const [checkin, setCheckin] = useState<HandoffDTO | null>(null);
  const dialog = checkin ? <CheckInDialog handoff={checkin} tabId={tabId} onClose={() => setCheckin(null)} onApplied={onApplied} /> : null;
  return { open: setCheckin, dialog };
}

/** Hand-offs on their own (all of them, or a sequence's), newest first; also those removed from the Import list. */
export function SentToEditList({ sequenceId, tabId, onApplied }: { sequenceId?: string; tabId?: string; onApplied?: (c: CheckinDTO) => void }) {
  const q = useHandoffs(sequenceId, { includeRemoved: true });
  const checkin = useCheckinDialog(tabId, onApplied);
  const items = q.data?.items ?? [];
  if (!items.length) return <p className="muted" style={{ margin: 0 }} data-testid="sent-to-edit-empty">Nothing sent to edit yet.</p>;
  return (
    <div className="steps" data-testid="sent-to-edit">
      {checkin.dialog}
      {items.map((h) => <HandoffRow key={h.id} h={h} onCheckin={checkin.open} />)}
    </div>
  );
}
